<script setup lang="ts">
import { toRefs } from 'vue';
import { IPolicy } from '../../types/policy.type';
import { formatDateAndHourSpanish } from '../../helpers/functionsMoment';
import { describeResponseMips } from '../../helpers/responseMips';
import useClipboard from 'vue-clipboard3';
import OperationsRepository from '@repositories/operations.repository';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();
interface Props {
  policyData: IPolicy;
}
const props = defineProps<Props>();
const { policyData } = toRefs(props);
const { toClipboard } = useClipboard();

const operationsRepository = new OperationsRepository();

const copyToClipboard = async (url: string) => {
  try {
    await toClipboard(url);
  } catch (e) {
    console.error(e);
  }
};

const downloadDocument = async () => {
  await operationsRepository.downloadClientDocument({
    clientId: policyData.value.clientId,
    policyId: policyData.value.id,
    documentPath: policyData.value.documentPath,
  });
};

</script>

<template>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.chofer') }}: </strong>
	  {{ policyData.driverName }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.telefono_chofer') }}: </strong>
	  {{ policyData.driverCellphone }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.placa_camion') }}: </strong>
	  {{ policyData.truckPlate }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.origen') }}: </strong>
	  {{ policyData.originLocation }} ({{ policyData.originRegionProvince }})
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.destino') }}: </strong>
	  {{ policyData.destinationLocation }} ({{ policyData.destinationRegionProvince }})
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.fecha_inicio_viaje') }}: </strong>
	  {{ formatDateAndHourSpanish(policyData.travelStartDate) }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.fecha_fin_viaje') }}: </strong>
	  {{ formatDateAndHourSpanish(policyData.travelEndDate) }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.cotizacion_id') }}: </strong>
	  {{ policyData.cotizacionId }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.poliza_id') }}: </strong>
	  {{ policyData.polizaId }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.numero_poliza') }}: </strong>
	  {{ policyData.claveId }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.precio_total') }}: </strong>
	  $ {{ policyData.fullPrice }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.limite_indemnizacion') }}: </strong>
	  $ {{ policyData.indemnificationLimit }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.cliente') }}: </strong>
	  {{ policyData.client.tradeName }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.direccion_cliente') }}: </strong>
	  {{ policyData.client.address }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.telefono_cliente') }}: </strong>
	  {{ policyData.client.phone1 }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.cuit_cliente') }}: </strong>
	  {{ policyData.client.cuit }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.ubicacion_cliente') }}: </strong>
	  {{ policyData.client.location }} ({{ policyData.client.regionProvince }})
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.descripcion_activo') }}: </strong>
	  {{ policyData.asset.description }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.tipo_riesgo') }}: </strong>
	  {{ policyData.asset.riskType.description }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.compania_gps') }}: </strong>
	  {{ policyData.companyGPS.description }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.pais') }}: </strong>
	  {{ policyData.country.description }}
	</p>
	<p>
	  <strong>{{ t('operaciones.modal_content_policy_ar.respuesta_mips') }}: </strong>
	  {{ describeResponseMips(policyData?.responseMips) }}
	</p>
	<div class="d-flex justify-content-between align-items-center my-3">
	  <p class="m-0">
		<strong>{{ t('operaciones.modal_content_policy_ar.documento') }}:</strong>
		{{ policyData.documentDescription }}
	  </p>
	  <CButton
		v-if="policyData.documentPath"
		class="background-blue button__form m-0"
		@click="downloadDocument"
	  >
		<template #content> {{ t('operaciones.modal_content_policy_ar.descargar_documento') }} </template>
	  </CButton>
	</div>
	<p v-if="policyData.urlPayment">
	  <strong>{{ t('operaciones.modal_content_policy_ar.url_pago') }}:</strong>
	  {{ policyData.urlPayment || t('operaciones.modal_content_policy_ar.sin_datos') }}

	  <CButton
		class="background-blue button__form m-0"
		@click="copyToClipboard(policyData.urlPayment)"
	  >
		<template #content> {{ t('operaciones.modal_content_policy_ar.copiar_url') }} </template>
	  </CButton>
	</p>
	<p v-if="policyData.invoiceId">
	  <strong>{{ t('operaciones.modal_content_policy_ar.invoice_id') }}: </strong>
	  {{ policyData.invoiceId || t('operaciones.modal_content_policy_ar.sin_datos') }}
	</p>
  </template>
